import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { 
  HiOutlineX, 
  HiOutlineCheck, 
  HiOutlineExclamation 
} from 'react-icons/hi';
import { updateUserProfile } from '../../store/slices/authSlice';
import './ProfileEditForms.scss';

const BiographyForm = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    bio: userData.bio || '',
    skills: userData.skills || '',
    interests: userData.interests || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add function to handle modal overlay click
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Only include fields that have actually changed
      const updatedData = Object.keys(formData).reduce((acc, key) => {
        if (formData[key] !== userData[key]) {
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      // Only dispatch if there are changes
      if (Object.keys(updatedData).length > 0) {
        await dispatch(updateUserProfile({
          email: userData.email,
          profileData: updatedData
        })).unwrap();
        setSuccess('Biography updated successfully!');
        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSuccess('No changes detected');
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating biography:', err);
      setError(err.message || 'Failed to update biography. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-edit-modal">
      <div className="modal-overlay" onClick={handleOverlayClick}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Edit Biography</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {error && (
          <div className="alert error">
            <HiOutlineExclamation />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <HiOutlineCheck />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-group">
            <label htmlFor="bio">Biography</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Tell us about yourself..."
              rows="6"
            />
            <small className="field-note">Write a short professional bio highlighting your experience and interests</small>
          </div>

          <div className="form-group">
            <label htmlFor="skills">Skills</label>
            <input
              type="text"
              id="skills"
              name="skills"
              value={formData.skills}
              onChange={handleChange}
              placeholder="Enter your skills"
            />
            <small className="field-note">Separate skills with commas (e.g., React, JavaScript, UI Design)</small>
          </div>

          <div className="form-group">
            <label htmlFor="interests">Interests</label>
            <input
              type="text"
              id="interests"
              name="interests"
              value={formData.interests}
              onChange={handleChange}
              placeholder="Enter your interests"
            />
            <small className="field-note">Separate interests with commas (e.g., Reading, Travel, Photography)</small>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BiographyForm; 